class OrgNavigation {
  constructor() {
    this.pendingNavigations = []
    this.routerContext = null
    this.baseUrl = null
  }

  setReactRouter(routerContext, baseUrl) {
    this.routerContext = routerContext
    this.baseUrl = baseUrl
    this.processPendingNavigations()
  }

  clearReactRouter() {
    this.routerContext = null
    this.baseUrl = null
  }

  get isReady() {
    return this.routerContext !== null && this.baseUrl !== null
  }

  shouldUseReactRouter(url) {
    return this.isOrgNav(url)
  }

  navigateViaReactRouter(url, preventDefault) {
    if (!this.isOrgNav(url))
      return Promise.resolve(false)

    const currentPageIsOrgNav = this.isOrgNav(this.getCurrentPathname())
    const targetIsOrgNav = true // We already checked this above

    // Special handling for non-orgnav → orgnav transitions
    if (!currentPageIsOrgNav && targetIsOrgNav) {
      if (this.pendingNavigations.length === 0) {
        // First click from non-orgnav to orgnav - let server load the container
        // Add a "loading marker" to track that we're loading the container
        this.pendingNavigations.push({url: null, resolve: null}) // Loading marker
        return Promise.resolve(false) // Let browser handle it
      }
      // Subsequent clicks while container is loading - queue them
      preventDefault()
      return new Promise((resolve) => {
        this.pendingNavigations.push({url, resolve})
      })
    }

    // Normal orgnav → orgnav transitions
    if (this.isReady) {
      preventDefault()
      this.routerContext.history.push(this.getOrgnavScopedPath(url))
      return Promise.resolve(true)
    }

    if (this.pendingNavigations.length > 0) {
      // Subsequent clicks while first navigation is pending
      preventDefault()
      return new Promise((resolve) => {
        this.pendingNavigations.push({url, resolve})
      })
    }

    // First navigation when router not ready - let browser handle it
    return new Promise((resolve) => {
      this.pendingNavigations.push({url, resolve})
    })
  }

  getOrgnavScopedPath(url) {
    return this.baseUrl ? url.replace(this.baseUrl, '') : url
  }

  processPendingNavigations() {
    if (this.pendingNavigations.length > 0 && this.isReady) {
      const allPending = [...this.pendingNavigations]
      this.pendingNavigations = []

      // Filter out loading markers and find actual navigations
      const realNavigations = allPending.filter(({url, resolve}) => url !== null && resolve !== null)

      if (realNavigations.length > 0) {
        const lastNavigation = realNavigations[realNavigations.length - 1]
        this.routerContext.history.push(this.getOrgnavScopedPath(lastNavigation.url))
        realNavigations.forEach(({resolve}) => resolve(true))
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  isOrgNav(url) {
    return (url || '').includes('/orgnav/')
  }

  // eslint-disable-next-line class-methods-use-this
  getCurrentPathname() {
    return window.location.pathname
  }

  getCurrentOrgnavPath() {
    // If we have React Router context and base URL, return the full orgnav path
    if (this.routerContext && this.routerContext.location && this.baseUrl)
      return this.baseUrl + this.routerContext.location.pathname

    // Otherwise fall back to window location
    return window.location.pathname
  }
}

// Create singleton instance
const orgNavigation = new OrgNavigation()

export default orgNavigation
export {OrgNavigation} // For testing only
